import React, { useEffect, useRef, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import {
  formatSeriesValue,
  getPriceFormat,
  formatAssetData,
  getBacktestOptions,
  IStats,
  createChart,
  getMarkers,
} from '../../utils/chart';
import { Box, Text, Heading, Select, Slider, Grid, Card, Input } from '../core';
import {
  getAssetData,
  getBacktestData,
  formatPercentage,
  BacktestType,
  AssetData,
} from '@sc1/common/utils';
import { useGraphAssets, useSearchParam } from '@sc1/common/hooks';
import { IChartApi, ISeriesApi } from 'lightweight-charts';
import { useThemedChart } from '../../hooks';
import { fmtLegendValue, Graph } from '../Graph';
import { GraphStats } from '../Graph/GraphStats';
import { base } from '../../styles/base';
import { SelectDataType } from './SelectDataType';

const chartId = 'backtest';

export const Backtest = (): JSX.Element => {
  // params
  const history = useHistory();
  const { position }: { position: BacktestType } = useParams();
  const { get } = useSearchParam();
  const asset: 'string' = get('asset');

  // options
  const [outdated, setOutdated] = useState(false);
  const [loading, setLoading] = useState(true);
  const [dataType, setDataType] = useState<AssetData>('price');
  const [rebalanceThreshold, setRebalanceThreshold] = useState(0.08);
  const [positionRange, setPositionRange] = useState<number>(0.15);
  const [days, setDays] = useState<string>('');

  const [[cursorSecondary, cursorPnl], setLegends] = useState<
    [number, number] | [null, null]
  >([0, 0]);

  const [stats, setStats] = useState<IStats>();

  const { assets, assetName } = useGraphAssets(position, asset);
  const chartStyles = useThemedChart();

  // refs
  const chartRef = useRef<IChartApi>();
  const secondSeriesRef = useRef<ISeriesApi<'Area'>>();
  const pnlSeriesRef = useRef<ISeriesApi<'Line'>>();

  const crosshairMoveHandler = (params) => {
    // updates legends
    const { seriesPrices, time } = params;
    const cursorSecondary = seriesPrices.get(secondSeriesRef.current);
    const cursorPnl = seriesPrices.get(pnlSeriesRef.current);
    if (!time) setLegends([null, null]);
    else setLegends([cursorSecondary, cursorPnl]);
  };

  const updateSecondarySeries = async (dataProp?: any) => {
    if (position === 'perpetual' && !dataProp) return; // perpetual endpoint handles asset data differently
    if (!chartRef.current) return;
    const assetData = !dataProp && (await getAssetData(dataType, asset));
    const data = dataProp || formatAssetData(assetData, dataType);

    if (secondSeriesRef.current && chartRef.current) {
      try {
        chartRef.current.removeSeries(secondSeriesRef.current);
      } catch (err) {
        console.log(err);
      }
    }

    const chart = chartRef.current;
    const secondaryLineSeries = chart.addAreaSeries({
      lineColor: '#2d50a9cc',
      bottomColor: 'rgba(38, 74, 166, 0.05)',
      topColor: 'rgba(38, 72, 166, 0.28)',
      priceScaleId: 'left',
      lineWidth: 2,
      priceFormat: getPriceFormat(dataType),
    });
    secondSeriesRef.current = secondaryLineSeries;
    if (!data?.length) return;
    secondaryLineSeries.setData(data);
    chart.timeScale().resetTimeScale();
    chart.timeScale().fitContent();
  };

  const updatePositionSeries = async (firstRenderOnTab?: boolean) => {
    if (firstRenderOnTab) setDays(position === 'perpetual' ? '180' : '365');
    setLoading(true);
    const chart = chartRef.current;
    if (!chart) return;
    const backtestOptions = getBacktestOptions({
      asset,
      position,
      days,
      rebalanceThreshold,
      positionRange,
    });
    handleSetDays(backtestOptions.days);
    const positionData = await getBacktestData(position, backtestOptions);
    // in the case of perpetual, this positionData also includes price data aka secondarySeries data.
    if (!positionData?.data?.length) return;

    if (!pnlSeriesRef.current) {
      pnlSeriesRef.current = chart.addLineSeries({
        color: base.colors.green,
        lineWidth: 2,
        priceFormat: {
          type: 'custom',
          formatter: (x) => formatPercentage(x / 100),
        },
      });
    }
    const data = positionData.data.map((d) => ({
      value: formatSeriesValue(d.value, position),
      time: d.timestamp,
    }));
    setStats(positionData.stats);
    pnlSeriesRef.current.setData(data);
    const markers = getMarkers(positionData.data);
    pnlSeriesRef.current.setMarkers(markers);

    if (position === 'perpetual') {
      const priceData = positionData?.data?.map((d) => ({
        value: d.price,
        time: d.timestamp, // dont divide this one by 1000
      }));
      if (priceData) await updateSecondarySeries(priceData);
    }
    setLoading(false);
    setOutdated(false);
    chart.timeScale().resetTimeScale();
    chart.timeScale().fitContent();
  };

  const handleSetDays = (x) => {
    if (x !== days) {
      setDays(x);
      setOutdated(true);
    }
  };

  useEffect(() => {
    // initialize graph
    const chart = createChart(chartId, chartStyles);
    chartRef.current = chart;
    chart.subscribeCrosshairMove(crosshairMoveHandler);
    return () => {
      // unsubscribe on unmount
      chart.unsubscribeCrosshairMove(crosshairMoveHandler);
    };
  }, []);

  useEffect(() => {
    // make sure current asset is in assets;
    const found = assets?.find((x) => x.value === asset);
    if (!assets?.length) return;
    if (!found) {
      history.push(`/backtest/${position}?asset=${assets[0].value}`);
    }
  }, [assets, asset]);

  useEffect(() => {
    // perpetual only uses price data
    if (position === 'perpetual') setDataType('price');
  }, [position, dataType]);

  useEffect(() => {
    // when position changes, reset days
    updatePositionSeries(true);
  }, [!chartRef.current, position]);

  useEffect(() => {
    // when asset changes dont reset days
    updatePositionSeries();
  }, [asset]);

  useEffect(() => {
    updateSecondarySeries();
  }, [!chartRef.current, asset, dataType]);

  return (
    <>
      <Card>
        <Heading mb={6}>
          {assetName}{' '}
          <Text sx={{ textTransform: 'capitalize' }}>
            {position} Strategy Backtesting
          </Text>
        </Heading>

        <Box mb={3}>
          <Grid columns={2} gap={4} mb={3}>
            <Box>
              <Text>Asset</Text>
              <Select
                value={asset}
                onChange={(x) =>
                  history.push(`/backtest/${position}?asset=${x.target.value}`)
                }
              >
                {assets &&
                  assets.map(({ label, value }) => (
                    <option key={value} value={value}>
                      {label}
                    </option>
                  ))}
              </Select>
            </Box>

            <Box>
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  updatePositionSeries();
                }}
              >
                <Text>Days</Text>
                <Input
                  type="number"
                  value={days}
                  onChange={(e) => handleSetDays(e.target.value)}
                />
              </form>
            </Box>

            <Box>
              <Text>
                Rebalance Threshold: {formatPercentage(rebalanceThreshold)}
              </Text>
              <Slider
                value={rebalanceThreshold}
                step={0.01}
                min={0}
                max={1}
                onChange={(e) => {
                  setRebalanceThreshold(Number(e.target.value));
                  setOutdated(true);
                }}
              />
            </Box>

            {position === 'perpetual' ? (
              <Box>
                <Text>
                  V3 Position Range: {formatPercentage(positionRange)}
                </Text>
                <Slider
                  value={positionRange}
                  step={0.01}
                  min={0}
                  max={1}
                  onChange={(e) => {
                    setPositionRange(Number(e.target.value));
                    setOutdated(true);
                  }}
                />
              </Box>
            ) : (
              ''
            )}
          </Grid>
        </Box>
        {position === 'perpetual' && (
          <Text mb={4}>
            *Backtest includes maker fees and external rewards and assumes .2%
            slippage + .1% fee on each rebalance
          </Text>
        )}
        <Graph
          chartId={chartId}
          legends={[
            {
              label: (
                <Text sx={{ textTransform: 'capitalize' }}>
                  Asset {dataType}
                </Text>
              ),
              value: fmtLegendValue(dataType, cursorSecondary),
              color: 'lightBlue',
            },
            {
              label: 'Profit/Loss',
              value: cursorPnl ? formatPercentage(cursorPnl / 100) : '',
              color: 'green',
            },
          ]}
          update={() => updatePositionSeries()}
          loading={loading}
          outdated={outdated}
        />
        {position !== 'perpetual' && (
          <SelectDataType setDataType={setDataType} dataType={dataType} />
        )}
        <GraphStats heading="Backtest Stats" stats={stats} />
      </Card>
    </>
  );
};
